import styled from "@emotion/styled";
import { homePage } from "Home/routes";
import { patientRegistrationPage, toPatientProfilHome } from "Patient/routes";
import { useAuthBroadcastSignal } from "Session";
import { useStore } from "effector-react";
import { memo } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Block,
  Button,
  Flex,
  Grid,
  HeroBanner,
  Input,
  LoaderWithOverlay,
  Logo,
  PasswordInput,
  Typography,
  UnifiedLink,
  color,
  errorNotification,
} from "ui-kit";
import loginBg from "./login-page.jpg";
import { logIn } from "./patientSession";

type LoginData = {
  email: string;
  password: string;
};

export const PatientLoginPage = memo(() => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useStore(logIn.pending);
  const { notifyOthersAboutLogIn } = useAuthBroadcastSignal();
  const logInPatient: SubmitHandler<LoginData> = ({ email, password }) => {
    if (isLoading) {
      // prevent multiple request sending
      // Therefore, generated session token stays valid
      return;
    }
    logIn({
      email,
      password,
    })
      .then(({ id: patientId }) => {
        // Use case: when anonymous user trying to access route that requires authentication,
        // redirect him back to that route after authentication
        const { from } = location.state || {
          from: { pathname: toPatientProfilHome(patientId) },
        };
        navigate(from);
      })
      .then(notifyOthersAboutLogIn)
      .catch((error: Error) => {
        errorNotification(error.message);
      });
  };

  return (
    <Grid gridTemplateColumns="30% 70%" minHeight="100vh">
      <Helmet>
        <title>Patient login</title>
      </Helmet>
      <LoginFormContainer>
        {isLoading && <LoaderWithOverlay />}
        <LoginForm flexDirection="column" justifyContent="space-between">
          <Block textAlign="left">
            <Link to={homePage}>
              <Logo fill={color.G200} width="188px" />
            </Link>
          </Block>
          <Flex alignItems="center">
            <LoginFormSection width="100%">
              <Block marginBottom={2} textAlign="center">
                <Typography as="h1" type="h2">
                  Log in
                </Typography>
              </Block>
              <form onSubmit={handleSubmit(logInPatient)}>
                <Block marginBottom={1.5}>
                  <Input
                    autoFocus
                    type="email"
                    label="Email"
                    placeholder="Email"
                    errorMessage={errors["email"]?.message}
                    hasErrors={!!errors["email"]}
                    isRequired
                    {...register("email", { required: "Please, enter email" })}
                  />
                </Block>
                <Block marginBottom={4}>
                  <PasswordInput
                    label="Password"
                    placeholder="Password"
                    errorMessage={errors["password"]?.message}
                    hasErrors={!!errors["password"]}
                    isRequired
                    {...register("password", {
                      required: "Please, enter password",
                    })}
                  />
                </Block>
                <Block textAlign="center">
                  <Button type="submit" filled>
                    Log in
                  </Button>
                </Block>
              </form>
            </LoginFormSection>
          </Flex>
          <Block>
            <Typography type="h4">Do not have an account?</Typography>{" "}
            <UnifiedLink to={patientRegistrationPage} type="h4">
              Sign up
            </UnifiedLink>
          </Block>
        </LoginForm>
      </LoginFormContainer>
      <Background height="100%" enableOverlay image={loginBg}>
        <AdvantagesSection
          gridTemplateColumns="100%"
          gridTemplateRows="max-content auto max-content"
        >
          <AdvantagesListContainer
            gridTemplateColumns="repeat(4, 1fr)"
            gridColumnGap={15.5}
            paddingLeft={3}
          >
            <Advantage type="h2" fontColor="N0">
              Security and privacy
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Certified doctors
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Easy to use system
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Money-back guarantee
            </Advantage>
          </AdvantagesListContainer>
          <Invitation>
            Log into account now and make an appointment with a doctor. Do not
            postpone your health problems for later.
          </Invitation>
          <Disclaimer paddingLeft={6}>
            <Typography fontSize="20px" fontColor="N0" fontWeight={400}>
              In the case of possible or actual emergency situations, do not use
              our service, but immediately call 911 or your local ambulance
              service.
            </Typography>
          </Disclaimer>
        </AdvantagesSection>
      </Background>
    </Grid>
  );
});

const Background = styled(HeroBanner)`
  height: 100%;
`;

const LoginFormContainer = styled(Flex)`
  background-color: ${color.N0};
  position: relative;
`;

const LoginForm = styled(Flex)`
  background-color: ${color.N0};
  padding: 72px 72px 32px;
  width: 100%;
`;

const LoginFormSection = Block.withComponent("section");

const AdvantagesSection = styled(Grid.withComponent("section"))`
  height: 100%;
  align-content: space-between;
  padding: 72px 56px 32px 72px;
`;

const AdvantagesListContainer = styled(Grid)`
  grid-column: 1 / end;
  grid-row: 1 / 1;
`;

const Advantage = styled(Typography)`
  align-self: center;
  padding-left: 32px;
  position: relative;
  &::before {
    border-radius: 50%;
    background-color: ${color.G200};
    content: "";
    display: inline-block;
    left: 0;
    margin-top: -10px;
    top: 50%;
    position: absolute;
    height: 20px;
    width: 20px;
  }
`;

const Invitation = styled(Block)`
  align-self: center;
  color: ${color.N0};
  font-family: Roboto, Arial, sans-serif;
  font-size: 50px;
  font-weight: 700;
  grid-column: 1 / end;
  grid-row: 2 / 3;
  line-height: 72px;
  padding-left: 48px;
`;

const Disclaimer = styled(Block)`
  grid-column: 1 / end;
  grid-row: 3 / 3;
`;
